import { ReactNode } from 'react';
import tw, { TwStyle } from 'twin.macro';

import { Loader } from '@components/Loader';
import { DoubleArrowToRight } from '@icons/DoubleArrowToRight';

const SidebarWrapper = tw.div`max-w-[371px] w-[371px] min-w-[371px] h-full transition-all ease-in-out duration-300 overflow-y-scroll overflow-x-hidden relative border-l border-gray-3 bg-neutral-background z-30 rounded-br shadow-ui`;
const SidebarHeader = tw.div`flex w-full h-0 min-h-0 border-b border-gray-3 justify-start items-center bg-neutral-background sticky top-0 z-30`;
const HeaderIconWrapper = tw.div`flex flex-col items-center justify-center h-0 w-[42px] cursor-pointer hover:bg-gray-2`;
const LoadingOverlay = tw.div` bg-neutral-background flex items-center justify-center z-10`;
const Divider = tw.div`w-full h-[1px] bg-gray-3 mt-[24px]`;

const SidebarItemContainer = tw.div`w-full relative`;
const ItemTitle = tw.h3`text-sm text-left text-text-and-icon leading-tight mb-1 font-semibold`;
const ItemSubtitle = tw.p`text-[11px] leading-snug text-text-and-icon-muted text-left`;
const SidebarItemsContainer = tw.div`p-[16px]`;
const StickyHeader = tw.div`sticky top-0 z-20 pt-3 pb-1 bg-neutral-background [&:stuck]:shadow-small`;
export const LargeTitle = tw.span`uppercase text-text-and-icon text-xs font-semibold`;

type SidebarSectionType = {
  header: string | JSX.Element | ReactNode | undefined;
  children: ReactNode;
  isLoading: boolean;
  hasDivider?: boolean;
};

const Title = ({ children }: { children: ReactNode }): JSX.Element => {
  return <ItemTitle>{children}</ItemTitle>;
};
const Subtitle = ({ children }: { children: ReactNode }): JSX.Element => {
  return <ItemSubtitle>{children}</ItemSubtitle>;
};

const collapsedSidebarStyles = tw`w-0 min-w-0 p-0` as TwStyle;

const Toggler = ({ onClick, ...rest }: { onClick: () => void }) => {
  return (
    <HeaderIconWrapper tw='border-l border-gray-3 overflow-hidden w-10' onClick={onClick} {...rest}>
      <DoubleArrowToRight tw='fill-text-and-icon' css={[tw`rotate-180`]} />
    </HeaderIconWrapper>
  );
};

export function SidebarItem({
  header,
  children,
  isLoading = false,
  hasDivider = false,
}: SidebarSectionType): JSX.Element {
  return (
    <SidebarItemContainer>
      <StickyHeader>{!!header && header}</StickyHeader>

      {isLoading ? (
        <LoadingOverlay>
          <Loader size='md' theme='dark' />
        </LoadingOverlay>
      ) : (
        <>{!!children && children}</>
      )}
      {hasDivider && <Divider />}
    </SidebarItemContainer>
  );
}

export const Sidebar = ({
  children,
  isVisible = true,
  header,
  onClick,
  closeable = false,
  customStyles,
  headerStyles,
}: {
  children: ReactNode;
  isVisible?: boolean;
  header?: ReactNode;
  onClick?: () => void;
  closeable?: boolean;
  customStyles?: TwStyle | undefined;
  headerStyles?: TwStyle | undefined;
}) => {
  return (
    <SidebarWrapper css={[!isVisible && collapsedSidebarStyles, customStyles]}>
      <SidebarHeader css={[headerStyles]}>
        {closeable && (
          <Toggler
            onClick={onClick as () => void}
            tw='rotate-180 mr-2 ml-[1px] rounded-l-md border-none hover:h-[calc(100% - 2px)]'
          />
        )}
        {header}
      </SidebarHeader>
      <SidebarItemsContainer>{children}</SidebarItemsContainer>
    </SidebarWrapper>
  );
};

Sidebar.Title = Title;
Sidebar.Subtitle = Subtitle;
Sidebar.Toggler = Toggler;
